import { useState } from "react";
import type { ActivityLog } from "@lifelog/shared";
import { api, ApiError } from "../lib/api.js";
import { ModalShell } from "./ModalShell.js";
import { TimeSelector } from "./TimeSelector.js";
import { ActivityIcon } from "./icons.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const NOTE_TYPE_ID = "note";

export function QuickNoteLog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (log: ActivityLog) => void;
}) {
  const [noteText, setNoteText] = useState("");
  const [occurredAt, setOccurredAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const text = noteText.trim();
    if (!text) {
      setError("Note text is required");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const log = await api<ActivityLog>("/logs", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        json: {
          activityTypeId: NOTE_TYPE_ID,
          occurredAt: occurredAt ?? undefined,
          values: {},
          note: text,
          source: "quick_button",
        },
      });
      onSaved(log);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save note");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      title="Add a note"
      icon={<ActivityIcon name="sticky-note" size={18} className="text-(--accent)" />}
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || !noteText.trim()}
            className="min-h-11 flex-1 rounded-lg bg-(--accent) text-sm font-medium text-(--accent-contrast) disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </p>
        )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Note</span>
          <textarea
            autoFocus
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="What happened?"
            rows={5}
            className={`${inputCls} resize-none`}
          />
        </label>
        <div>
          <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">When</span>
          <TimeSelector value={occurredAt} onChange={setOccurredAt} />
        </div>
      </div>
    </ModalShell>
  );
}
